/**
 * Automation (pivot doc §5, §6 Stage 4): workflows built from a trigger, a
 * short list of conditions and one or more actions, plus the queue and
 * history that follow from running them. No engine executes anything here —
 * run history and scheduled actions are generated, the way a listing's
 * pacing is generated from the same demo dataset the rest of the app reads.
 *
 * Workflows are generated one-for-one from each tenant fixture's
 * `automations` list (`TenantFixture.automations`), so a workflow's status
 * always agrees with the Overview's "Automations running" KPI, which counts
 * the same fixtures.
 */

import type { ModuleKey } from '@/api/types'
import type { DrillDown, EntityRef } from '@/lib/drilldown'

import type { AutomationStatus, Envelope } from './types'

export type { AutomationStatus }

// --- trigger catalogue -------------------------------------------------------

export const TRIGGER_FAMILIES = ['competition', 'analytics', 'market', 'marketing'] as const
export type TriggerFamily = (typeof TRIGGER_FAMILIES)[number]

export const TRIGGER_FAMILY_LABELS: Record<TriggerFamily, string> = {
  competition: 'Competition',
  analytics: 'Analytics thresholds',
  market: 'Market intelligence',
  marketing: 'Marketing',
}

export const TRIGGER_KEYS = [
  'competition_published',
  'competition_pct_sold',
  'competition_behind_pace',
  'competition_hours_to_close',
  'competition_sold_out',
  'competition_ended',
  'analytics_threshold',
  'market_competitor_launch',
  'market_price_change',
  'marketing_basket_abandoned',
  'marketing_campaign_milestone',
] as const
export type TriggerKey = (typeof TRIGGER_KEYS)[number]

export interface TriggerDefinition {
  key: TriggerKey
  family: TriggerFamily
  label: string
  description: string
  /** The entitlement module a tenant needs for this trigger to fire. */
  module: ModuleKey
}

/**
 * Keyed by `TriggerKey` rather than a plain array: adding a key to
 * `TRIGGER_KEYS` without a matching entry here is then a compile error
 * instead of a lookup that silently falls back to an unrelated trigger.
 */
export const TRIGGER_DEFINITIONS: Record<TriggerKey, TriggerDefinition> = {
  competition_published: {
    key: 'competition_published',
    family: 'competition',
    label: 'Competition published',
    description: 'A competition listing is published to the website.',
    module: 'competition_operations',
  },
  competition_pct_sold: {
    key: 'competition_pct_sold',
    family: 'competition',
    label: 'Reaches a percentage sold',
    description: 'A competition reaches a chosen share of its entries sold.',
    module: 'competition_operations',
  },
  competition_behind_pace: {
    key: 'competition_behind_pace',
    family: 'competition',
    label: 'Falls behind pace',
    description: 'A competition is selling more slowly than it needs to close on time.',
    module: 'competition_operations',
  },
  competition_hours_to_close: {
    key: 'competition_hours_to_close',
    family: 'competition',
    label: 'Hours to close',
    description: 'A chosen number of hours remain before a competition closes.',
    module: 'competition_operations',
  },
  competition_sold_out: {
    key: 'competition_sold_out',
    family: 'competition',
    label: 'Sells out',
    description: 'A competition reaches its maximum entries.',
    module: 'competition_operations',
  },
  competition_ended: {
    key: 'competition_ended',
    family: 'competition',
    label: 'Ends',
    description: 'A competition reaches its closing date, sold out or not.',
    module: 'competition_operations',
  },
  analytics_threshold: {
    key: 'analytics_threshold',
    family: 'analytics',
    label: 'Metric crosses a threshold',
    description: 'A chosen metric moves past a value over a time window, e.g. payment failure rate above 8% in 24 hours.',
    module: 'core_analytics',
  },
  market_competitor_launch: {
    key: 'market_competitor_launch',
    family: 'market',
    label: 'Competitor launch',
    description: 'A tracked competitor launches a new competition.',
    module: 'market_intelligence',
  },
  market_price_change: {
    key: 'market_price_change',
    family: 'market',
    label: 'Competitor price change',
    description: 'A tracked competitor changes a ticket price.',
    module: 'market_intelligence',
  },
  marketing_basket_abandoned: {
    key: 'marketing_basket_abandoned',
    family: 'marketing',
    label: 'Basket abandoned',
    description: 'A basket is left for a chosen length of time without checking out.',
    module: 'marketing_intelligence',
  },
  marketing_campaign_milestone: {
    key: 'marketing_campaign_milestone',
    family: 'marketing',
    label: 'Campaign milestone',
    description: 'A campaign reaches a spend or budget milestone.',
    module: 'marketing_intelligence',
  },
}

export const TRIGGER_DEFINITION_LIST: readonly TriggerDefinition[] = Object.values(TRIGGER_DEFINITIONS)

export function triggerDefinition(key: TriggerKey): TriggerDefinition {
  return TRIGGER_DEFINITIONS[key]
}

// --- action catalogue --------------------------------------------------------

export const ACTION_KEYS = [
  'create_alert',
  'notify_team',
  'schedule_campaign',
  'generate_report',
  'create_recommendation',
  'trigger_publishing',
  'send_email',
  'send_push',
] as const
export type ActionKey = (typeof ACTION_KEYS)[number]

export type ActionKind = 'internal' | 'channel'

export interface ActionDefinition {
  key: ActionKey
  label: string
  description: string
  kind: ActionKind
}

/** Keyed by `ActionKey`, for the same reason as `TRIGGER_DEFINITIONS`. */
export const ACTION_DEFINITIONS: Record<ActionKey, ActionDefinition> = {
  create_alert: { key: 'create_alert', label: 'Create an alert', description: 'Raises an alert in Insights for someone to review.', kind: 'internal' },
  notify_team: { key: 'notify_team', label: 'Notify the team', description: 'Posts a notification for the team to see.', kind: 'internal' },
  schedule_campaign: { key: 'schedule_campaign', label: 'Schedule a campaign', description: 'Schedules a marketing campaign to launch.', kind: 'internal' },
  generate_report: { key: 'generate_report', label: 'Generate a report', description: 'Runs a saved report and prepares it for export.', kind: 'internal' },
  create_recommendation: {
    key: 'create_recommendation',
    label: 'Create a recommendation',
    description: 'Adds a suggested action to Insights, for approval.',
    kind: 'internal',
  },
  trigger_publishing: { key: 'trigger_publishing', label: 'Trigger publishing', description: 'Publishes a listing to its configured targets.', kind: 'internal' },
  send_email: { key: 'send_email', label: 'Send an email', description: 'Sends an email to the affected customer or segment.', kind: 'channel' },
  send_push: {
    key: 'send_push',
    label: 'Send a push notification',
    description: 'Sends a push notification to the affected customer or segment.',
    kind: 'channel',
  },
}

export const ACTION_DEFINITION_LIST: readonly ActionDefinition[] = Object.values(ACTION_DEFINITIONS)

export function actionDefinition(key: ActionKey): ActionDefinition {
  return ACTION_DEFINITIONS[key]
}

// --- workflows ----------------------------------------------------------------

export interface WorkflowTrigger {
  key: TriggerKey
  family: TriggerFamily
  label: string
  module: ModuleKey
  /** Plain-English detail specific to this workflow, e.g. "80% sold" or "24 hours". */
  detail: string
  params: Record<string, string>
}

export type ConditionOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains'

export interface WorkflowCondition {
  field: string
  label: string
  operator: ConditionOperator
  value: string
}

export interface WorkflowAction {
  key: ActionKey
  label: string
  detail: string
}

export interface Workflow {
  id: string
  name: string
  status: AutomationStatus
  trigger: WorkflowTrigger
  conditions: WorkflowCondition[]
  actions: WorkflowAction[]
  lastRunAt: string | null
  runCount: number
  createdBy: string
  createdAt: string
  updatedAt: string
}

// --- scheduled actions ---------------------------------------------------------

export const SCHEDULED_ACTION_STATES = ['pending', 'completed', 'cancelled', 'failed'] as const
export type ScheduledActionState = (typeof SCHEDULED_ACTION_STATES)[number]

export interface ScheduledAction {
  id: string
  workflowId: string
  workflowName: string
  action: WorkflowAction
  dueAt: string
  state: ScheduledActionState
  entity: EntityRef | null
  drillDown: DrillDown
}

// --- activity -------------------------------------------------------------------

export const ACTIVITY_OUTCOMES = ['success', 'failure', 'skipped'] as const
export type ActivityOutcome = (typeof ACTIVITY_OUTCOMES)[number]

export interface ActivityEntry {
  id: string
  workflowId: string
  workflowName: string
  at: string
  outcome: ActivityOutcome
  summary: string
  entity: EntityRef | null
  drillDown: DrillDown
}

// --- the API slice --------------------------------------------------------------

/**
 * Automation's slice of `ApiClient`. `triggerDefinitions` / `actionDefinitions`
 * are async for symmetry with everything else here, even though the mock
 * answers them from a static catalogue.
 */
export interface AutomationApi {
  triggerDefinitions(): Promise<TriggerDefinition[]>
  actionDefinitions(): Promise<ActionDefinition[]>
  workflows(): Promise<Envelope<Workflow[]>>
  workflow(id: string): Promise<Envelope<Workflow | null>>
  setWorkflowStatus(id: string, status: AutomationStatus): Promise<Envelope<Workflow>>
  scheduledActions(): Promise<Envelope<ScheduledAction[]>>
  cancelScheduledAction(id: string): Promise<Envelope<ScheduledAction[]>>
  automationActivity(): Promise<Envelope<ActivityEntry[]>>
}
